import React from "react";
import { Box, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BondRetrievalPageLayout from "../../BondRetrievalPageLayout";
import { getBondRetrievalShellConfig } from "./bondRetrievalShellConfig";
import { BondRetrievalInternalPanels } from "./BondRetrievalInternalPanels";
import { BondRetrievalActionFooter } from "./BondRetrievalActionFooter";
import { BondRetrievalChatHistoryOverlays } from "./BondRetrievalChatHistoryOverlays";
import { BondRetrievalRejectDialog } from "./BondRetrievalRejectDialog";
import { BondRetrievalCreditReportDialog } from "./BondRetrievalCreditReportDialog";
import { BondRetrievalUnderwritingDialogs } from "./BondRetrievalUnderwritingDialogs";
import { BondRetrievalDataCorrectionDialog } from "./BondRetrievalDataCorrectionDialog";
import { DATA_CORRECTION_FORM_BY_KIND } from "./BondRetrievalDataCorrectionForms";

/**
 * Shared orchestrator for court bond retrieval page shells (Phase 9.9A).
 */
export function BondRetrievalShell({
  kind,
  sectionConfig,
  pageCtx,
  onSubmitDataCorrection,
  formatCorrectionFieldLabel: formatFieldLabelProp,
  formatCorrectionFromTo: formatFromToProp,
  bond_id,
  isLoading,
  isEsignatureLoading,
  isSubmitLoading,
  billingMethod,
  setBillingMethod,
  isIndemnityReceived,
  setIsIndemnityReceived,
  isApproveLoading,
  isRejectLoading,
  openChat,
  setOpenChat,
  chatUnreadTotal,
  isHistoryOpen,
  setIsHistoryOpen,
  endorsementLogs,
  isHistoryLoading,
  historyTab,
  setHistoryTab,
  data,
  bondUpdates,
  role,
  disabled: _disabled,
  username,
  isCreditDialogOpen,
  setIsCreditDialogOpen,
  rejectDialogOpen,
  setRejectDialogOpen,
  rejectFcraFile,
  setRejectFcraFile,
  rejectFcraUploading,
  setRejectFcraUploading,
  creditReportFile,
  setCreditReportFile,
  isCreditUploading,
  setIsCreditUploading,
  editableCreatedAt,
  setEditableCreatedAt,
  anchorEl,
  globalState,
  userdetails: _userdetails,
  handleFinish,
  handleSignIndemnity,
  handleReject,
  isUnderwritingLoading,
  handleUpdateUnderwriting,
  handleButtonClick,
  handleMenuClose,
  handleOptionSelect,
  underwritingAnchorEl,
  overrideReasonDialogOpen,
  setOverrideReasonDialogOpen,
  overrideReason,
  setOverrideReason,
  manualUnderwritingDialogOpen,
  setManualUnderwritingDialogOpen,
  manualUnderwritingReason,
  setManualUnderwritingReason,
  handleUnderwritingMenuClick,
  handleUnderwritingMenuClose,
  handleOverrideUnderwritingClick,
  handleManualUnderwritingClick,
  handleSubmitManualUnderwriting,
  handleSubmitOverrideReason,
  runCreditReport,
  isDataCorrectionOpen,
  setIsDataCorrectionOpen,
  dataCorrectionTab,
  setDataCorrectionTab,
  bondDataCorrectionsList,
  bondDataCorrectionsLoading,
  isSubmitting,
  counties,
  loadingCounties,
  dataCorrectionForm,
  setDataCorrectionForm,
  handleOpenDataCorrection,
  handleBondAmountChange,
  toggleHistoryDrawer,
  uploadToADLS,
}) {
  const shellConfig = getBondRetrievalShellConfig(kind);
  const DataCorrectionForm = DATA_CORRECTION_FORM_BY_KIND[kind];
  const fieldLabelFormatter = formatFieldLabelProp;
  const fromToFormatter = formatFromToProp;

  const signIndemnityDisabled = shellConfig.signIndemnityDisabled({
    isEsignatureLoading,
    data,
    globalState,
  });

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectFcraFile(null);
  };

  const closeCreditDialog = () => {
    setIsCreditDialogOpen(false);
  };

  const closeDataCorrectionDialog = () => {
    setIsDataCorrectionOpen(false);
    setDataCorrectionTab(0);
  };

  return (
    <BondRetrievalPageLayout
      title={sectionConfig.title}
      data={data}
      role={role}
      isLoading={isLoading}
      footer={
        <BondRetrievalActionFooter
          role={role}
          isSubmitLoading={isSubmitLoading}
          isApproveLoading={isApproveLoading}
          isRejectLoading={isRejectLoading}
          isEsignatureLoading={isEsignatureLoading}
          isUnderwritingLoading={isUnderwritingLoading}
          data={data}
          anchorEl={anchorEl}
          underwritingAnchorEl={underwritingAnchorEl}
          signIndemnityDisabled={signIndemnityDisabled}
          esignatureButtonLabel={shellConfig.esignatureButtonLabel}
          handleFinish={handleFinish}
          handleButtonClick={handleButtonClick}
          handleMenuClose={handleMenuClose}
          handleOptionSelect={handleOptionSelect}
          setRejectDialogOpen={setRejectDialogOpen}
          handleUnderwritingMenuClick={handleUnderwritingMenuClick}
          handleUnderwritingMenuClose={handleUnderwritingMenuClose}
          handleManualUnderwritingClick={handleManualUnderwritingClick}
          handleOverrideUnderwritingClick={handleOverrideUnderwritingClick}
          setIsCreditDialogOpen={setIsCreditDialogOpen}
          handleSignIndemnity={handleSignIndemnity}
          handleUpdateUnderwriting={handleUpdateUnderwriting}
        />
      }
      overlays={
        <>
          <BondRetrievalChatHistoryOverlays
            role={role}
            bond_id={bond_id}
            username={username}
            openChat={openChat}
            setOpenChat={setOpenChat}
            chatUnreadTotal={chatUnreadTotal}
            isHistoryOpen={isHistoryOpen}
            setIsHistoryOpen={setIsHistoryOpen}
            endorsementLogs={endorsementLogs}
            isHistoryLoading={isHistoryLoading}
            historyTab={historyTab}
            setHistoryTab={setHistoryTab}
            bondUpdates={bondUpdates}
            toggleHistoryDrawer={toggleHistoryDrawer}
            historyUpdatesVariant={shellConfig.historyUpdatesVariant}
          />

          <BondRetrievalRejectDialog
            open={rejectDialogOpen}
            onClose={closeRejectDialog}
            rejectFcraFile={rejectFcraFile}
            setRejectFcraFile={setRejectFcraFile}
            rejectFcraUploading={rejectFcraUploading}
            setRejectFcraUploading={setRejectFcraUploading}
            isRejectLoading={isRejectLoading}
            handleReject={handleReject}
            uploadToADLS={uploadToADLS}
          />

          <BondRetrievalCreditReportDialog
            open={isCreditDialogOpen}
            onClose={closeCreditDialog}
            bond_id={bond_id}
            creditReportFile={creditReportFile}
            setCreditReportFile={setCreditReportFile}
            isCreditUploading={isCreditUploading}
            setIsCreditUploading={setIsCreditUploading}
            runCreditReport={runCreditReport}
          />

          <BondRetrievalDataCorrectionDialog
            open={isDataCorrectionOpen}
            onClose={closeDataCorrectionDialog}
            dataCorrectionTab={dataCorrectionTab}
            setDataCorrectionTab={setDataCorrectionTab}
            bondDataCorrectionsList={bondDataCorrectionsList}
            bondDataCorrectionsLoading={bondDataCorrectionsLoading}
            isSubmitting={isSubmitting}
            onSubmit={onSubmitDataCorrection}
            formatCorrectionFieldLabel={fieldLabelFormatter}
            formatCorrectionFromTo={fromToFormatter}
            renderCorrectDataTab={() => (
              <DataCorrectionForm
                counties={counties}
                loadingCounties={loadingCounties}
                dataCorrectionForm={dataCorrectionForm}
                setDataCorrectionForm={setDataCorrectionForm}
                handleBondAmountChange={handleBondAmountChange}
                originalBondAmount={data?.bond_amount}
              />
            )}
          />

          <BondRetrievalUnderwritingDialogs
            manualUnderwritingDialogOpen={manualUnderwritingDialogOpen}
            setManualUnderwritingDialogOpen={setManualUnderwritingDialogOpen}
            manualUnderwritingReason={manualUnderwritingReason}
            setManualUnderwritingReason={setManualUnderwritingReason}
            handleSubmitManualUnderwriting={handleSubmitManualUnderwriting}
            overrideReasonDialogOpen={overrideReasonDialogOpen}
            setOverrideReasonDialogOpen={setOverrideReasonDialogOpen}
            overrideReason={overrideReason}
            setOverrideReason={setOverrideReason}
            handleSubmitOverrideReason={handleSubmitOverrideReason}
            isUnderwritingLoading={isUnderwritingLoading}
          />
        </>
      }
    >
      {shellConfig.showDataCorrectionButton(role) && (
        <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenDataCorrection}
          >
            Data Correction
          </Button>
        </Box>
      )}

      {!isLoading && (
        <Box className="mb-6">
          {sectionConfig.renderSections(pageCtx)}
          <BondRetrievalInternalPanels
            role={role}
            billingMethod={billingMethod}
            setBillingMethod={setBillingMethod}
            isIndemnityReceived={isIndemnityReceived}
            setIsIndemnityReceived={setIsIndemnityReceived}
            editableCreatedAt={editableCreatedAt}
            setEditableCreatedAt={setEditableCreatedAt}
            data={data}
          />
        </Box>
      )}
    </BondRetrievalPageLayout>
  );
}

export default BondRetrievalShell;
